import type { GeneralInboundRow } from './useGetInboundList'

/**
 * 필수 입력 컬럼 — 1.0 GR01 그리드 메타(gridsub `columnDefs[].required`) 7칸과 같은 목록이다(센터명은 1.0 에서 필수가 아니다 — 사용자 결정 2026-10-01).
 *
 * 1.0 은 CommonUtil.onSaveData 가 컬럼 정의의 `required` 를 훑어 빈 칸이 있으면 행 번호와
 * 컬럼명을 알리고 저장을 중단했다. 그 파리티를 유지하되, 표 컬럼과 저장 전 검사가 갈라지지 않게
 * 목록을 여기 한 곳에 둔다. 표(consts.ts)는 이 목록에서 `required` 표시를 파생하고,
 * 저장 전 검사(useModifyInbounds.validateRows)도 같은 목록을 훑는다.
 *
 * 런타임 의존이 없는 모듈로 떼어 뒀다. 표 상수에서 데이터 레이어를 통째로 끌고 오면
 * 화면과 무관한 모듈(axios·react-query)까지 딸려온다.
 */
export const REQUIRED_FIELDS = [
  { key: 'SPR_NM', label: '화주코드' },
  { key: 'PLN_DATE', label: '입고예정일자' },
  { key: 'ORD_OPT_NO', label: '입고번호' },
  { key: 'GDS_NM', label: '상품명' },
  { key: 'ORD_QTY', label: '수량' },
  { key: 'WH_AREA', label: '층정보' },
  { key: 'BARCODE', label: '바코드' },
] as const satisfies ReadonlyArray<{ readonly key: keyof GeneralInboundRow; readonly label: string }>

/** 1.0 GR01 운영 메타에서 UID·옵션코드를 노출하고 필수로 받는 화주. 샘플 화주는 포함하지 않는다. */
const NIKE_IDENTIFIER_SHIPPERS: ReadonlySet<string> = new Set(['NIKE', '29CM_NIKE', 'MUSINSA_USED'])

export const NIKE_IDENTIFIER_FIELDS = [
  { key: 'UID', label: 'UID' },
  { key: 'option_code', label: '옵션코드' },
] as const satisfies ReadonlyArray<{ readonly key: keyof GeneralInboundRow; readonly label: string }>

/**
 * UID·옵션코드 복원(LOGISPRD-16423) kill switch. ON=NIKE 계열 화주에 두 컬럼 노출·필수검사·저장 값 전송, SKUID 비노출.
 * OFF·미등록·조회 실패=복원 전 동작(UID·옵션코드 없음, SKUID 노출). 아래 함수들의 `enabled` 인자로 이 값을 넘긴다.
 * 인자 기본값 true 는 정책 자체를 검증하는 단위 테스트용이다 — 화면(GeneralInboundTable)은 늘 플래그 값을 넘긴다.
 */
export const NIKE_IDENTIFIERS_FLAG = 'hotfix.moms.inbound.restoreNikeIdentifierColumns' as const

export const usesNikeIdentifiers = (shipperCode?: string, enabled = true): boolean =>
  enabled && NIKE_IDENTIFIER_SHIPPERS.has(shipperCode ?? '')

export type RequiredField = { readonly key: keyof GeneralInboundRow; readonly label: string }

/**
 * base 는 화면관리 그리드 설정(GR07_HEAD)을 반영한 필수 목록이다(LOGISPRD-17250) — 없으면 1.0 고정 목록.
 * NIKE 식별자 두 칸은 설정과 무관하게 화주별로 더한다(LOGISPRD-16423 정책).
 */
export const requiredFieldsFor = (
  shipperCode?: string,
  enabled = true,
  base: ReadonlyArray<RequiredField> = REQUIRED_FIELDS,
): ReadonlyArray<RequiredField> => (usesNikeIdentifiers(shipperCode, enabled) ? [...base, ...NIKE_IDENTIFIER_FIELDS] : base)
